import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Trader Header Component
 * Top navigation bar with account info box, language, and notifications
 * Matches reference design: [Account ID | Currency | Type] in a bordered box
 */
import { useState } from 'react';
import { useTraderAuth } from '../context/TraderAuthContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useMobileNav } from '../context/MobileNavContext';
import { Bell, ChevronDown, Menu, LayoutGrid, Smartphone } from 'lucide-react';
import {AccountSwitcher,CustomerNotifications} from './CustomerAccountTools';
import {NotificationBadge} from '../notifications/NotificationContext';
import {TenantBrand,useBranding} from '../../branding/BrandingContext';
import { usePWAInstall } from '../hooks/usePWAInstall';
import PWAInstallModal from './PWAInstallModal';

const TraderHeader = () => {
  const tx = useTraderText();
  const {brand}=useBranding();
  const { user } = useTraderAuth();
  const { isDark } = useTraderTheme();
  const { t, language, setLanguage } = useTraderLanguage();
  const { setSidebarOpen, setWatchlistOpen, isMobile, isTablet } = useMobileNav();
  
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { isInstalled, isIOS, promptInstall, showIOSModal, setShowIOSModal } = usePWAInstall();
  
  const selectedCurrency = user?.currency || '—';
  const selectedType = user?.account_type?.toLowerCase() === 'demo' ? 'Demo' : user?.account_type?.toLowerCase() === 'real' ? 'Real' : '—';

  const currencies = ['USD', 'TRY', 'EUR', 'GBP'];
  const accountTypes = ['Real', 'Demo'];
  const languages = [
    { code: 'en', flag: '🇬🇧', label: 'EN' },
    { code: 'tr', flag: '🇹🇷', label: 'TR' }
  ];

  const currentLang = languages.find(l => l.code === language) || languages[0];
  const accountId = user?.account_id || user?.id?.slice(0, 8).toUpperCase() || 'N/A';

  const closeAllDropdowns = () => {
    setShowAccountDropdown(false);
    setShowLangDropdown(false);
    setShowNotifications(false);
  };

  return (
    <header data-testid="trader-header" className={`mt5-header h-14 flex items-center justify-between px-2 sm:px-4 border-b ${
      isDark ? 'bg-[#0f1724] border-gray-800' : 'bg-white border-gray-200'
    }`}>
      {/* Left side - Logo + Menu */}
      <div className="flex items-center gap-3">
        {/* Mobile menu button */}
        {(isMobile || isTablet) && (
          <button
            onClick={() => setSidebarOpen(true)}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
        )}

        {/* Logo */}
        {brand?<TenantBrand id="trader-header-brand" compact/>:<span data-testid="trader-header-brand" className="mt5-brand">E1 <b>Trader</b></span>}
      </div>

      {/* Center - Account Info Box */}
      <div className="relative">
        <button
          onClick={() => {
            closeAllDropdowns();
            setShowAccountDropdown(!showAccountDropdown);
          }}
          className={`flex items-center gap-1 sm:gap-3 px-2 sm:px-4 py-2 rounded-lg border transition-colors ${
            isDark 
              ? 'border-gray-700 hover:border-gray-600 bg-[#1a2332]' 
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
          data-testid="account-info-box"
        >
          {/* Account ID */}
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className={`hidden sm:inline text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Account ID")}</span>
            <span data-testid="trader-account-id" className={`font-mono font-medium text-xs sm:text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {accountId}
            </span>
          </div>

          {/* Divider */}
          <div className={`h-4 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>

          {/* Currency */}
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className={`hidden sm:inline text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Currency")}</span>
            <span data-testid="trader-account-currency" className={`font-medium text-xs sm:text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {selectedCurrency}
            </span>
          </div>

          {/* Divider */}
          <div className={`h-4 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>

          {/* Type */}
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className={`hidden sm:inline text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Type")}</span>
            <span data-testid="trader-account-type" className={`font-medium text-xs sm:text-sm ${
              selectedType === 'Demo' 
                ? 'text-yellow-400' 
                : isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {tx(selectedType)}
            </span>
          </div>

          <ChevronDown size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
        </button>
        
        {/* Dropdown */}
        {showAccountDropdown && (
          <div data-testid="trader-account-dropdown" className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 rounded-lg border shadow-xl z-50 ${
            isDark ? 'bg-[#1a2332] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {/* Currency Section */}
            <AccountSwitcher onClose={()=>setShowAccountDropdown(false)}/>
            <div className={`p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Currency")}</p>
              <div className="flex flex-wrap gap-2">
                {currencies.map((curr) => (
                  <button
                    key={curr}
                    disabled={selectedCurrency !== curr}
                    aria-pressed={selectedCurrency === curr}
                    data-testid={`trader-account-currency-${curr.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded text-sm transition-colors ${
                      selectedCurrency === curr
                        ? 'bg-cyan-500 text-white'
                        : isDark
                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {curr}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Account Type Section */}
            <div className="p-3">
              <p className={`text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tx("Account Type")}</p>
              <div className="flex gap-2">
                {accountTypes.map((type) => (
                  <button
                    key={type}
                    disabled={selectedType !== type}
                    aria-pressed={selectedType === type}
                    data-testid={`trader-account-type-${type.toLowerCase()}`}
                    className={`flex-1 px-3 py-1.5 rounded text-sm transition-colors ${
                      selectedType === type
                        ? type === 'Demo' 
                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                          : 'bg-cyan-500 text-white'
                        : isDark
                          ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tx(type)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right side - Language, Watchlist toggle (mobile), Notifications */}
      <div className="flex items-center gap-2">
        {/* Watchlist toggle for mobile */}
        {(isMobile || isTablet) && (
          <button
            onClick={() => setWatchlistOpen(true)}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            data-testid="watchlist-toggle"
          >
            <LayoutGrid size={20} />
          </button>
        )}

        {/* Language Selector */}
        <div className="relative">
          <button
            onClick={() => {
              closeAllDropdowns();
              setShowLangDropdown(!showLangDropdown);
            }}
            aria-label={tx('Language')}
            className={`flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
              isDark 
                ? 'border-gray-700 hover:border-gray-600 bg-[#1a2332]' 
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
            data-testid="language-dropdown"
          >
            <span className="text-xs font-semibold">{currentLang.label}</span>
          </button>
          
          {showLangDropdown && (
            <div data-testid="trader-language-menu" className={`absolute top-full right-0 mt-2 w-32 rounded-lg border shadow-xl z-50 ${
              isDark ? 'bg-[#1a2332] border-gray-700' : 'bg-white border-gray-200'
            }`}>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  data-testid={`trader-language-${lang.code}`}
                  onClick={() => {
                    setLanguage(lang.code);
                    setShowLangDropdown(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
                    language === lang.code
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : isDark
                        ? 'text-white hover:bg-gray-700'
                        : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{tx(lang.label)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              closeAllDropdowns();
              setShowNotifications(!showNotifications);
            }}
            className={`relative p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
            data-testid="notifications-btn"
            aria-label={language==='tr'?'Bildirimler':'Notifications'}
            aria-expanded={showNotifications}
          >
            <Bell size={20} />
            <NotificationBadge id="trader-notification-unread-badge"/>
          </button>
          
          {showNotifications && (
            <div data-testid="trader-notifications-popover" className={`trader-notifications-popover border ${
              isDark ? 'bg-[#1a2332] border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className={`p-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {tx(language==='tr'?'Bildirimler':'Notifications')}
                </h3>
              </div>
              <CustomerNotifications/>
            </div>
          )}
        </div>

        {/* Install App Button (PWA) */}
        {!isInstalled && (
          <button
            onClick={promptInstall}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
              isDark
                ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 shadow-sm'
                : 'border-cyan-400/50 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 shadow-sm'
            }`}
            data-testid="header-install-app-btn"
            title={language === 'tr' ? 'Uygulamayı Yükle / Çekmeceye Ekle' : 'Install App / Add to App Tray'}
          >
            <Smartphone size={15} />
            <span className="hidden md:inline">{tx(language === 'tr' ? 'Uygulamayı Yükle' : 'Install App')}</span>
          </button>
        )}
      </div>

      {/* Click outside handler */}
      {(showAccountDropdown || showLangDropdown || showNotifications) && (
        <div 
          data-testid="trader-header-dismiss"
          className="fixed inset-0 z-40"
          onClick={closeAllDropdowns}
        />
      )}

      {/* PWA Install Modal */}
      <PWAInstallModal
        isOpen={showIOSModal}
        onClose={() => setShowIOSModal(false)}
        isIOS={isIOS}
      />
    </header>
  );
};

export default TraderHeader;
